import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslateService } from '@ngx-translate/core';

import { ModalComponent } from 'projects/component-library/components/modal/modal.component';
import { ExpectedError, HttpErrorResponse } from 'projects/shared/services/models/error.response';
import { UpdateBulkUsersResponse, UsersToUpdate } from 'projects/shared/services/inspection-companies/inspection-company.user.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'shared-edit-users-bulk-user-modal',
  standalone: false,
  template: ` <mac-modal [(open)]="isVisible" #modal (openChange)="resetAllFormControls()">
    <mac-modal-title>
      {{ 'Portal.EditUserModal.Title' | translate }}
    </mac-modal-title>

    <mac-modal-content>
      <form class="company-form">
        <nz-form-control [nzXs]="24">
          <span class="upload-modal-info"
            >{{ 'Portal.InspectionCompanies.Activate.Form.EditMultipleUsers' | translate }}
            <a (click)="downloadBulkExample()">{{ 'Portal.InspectionCompanies.Activate.Form.Bulk.DownloadTemplate' | translate }}</a>
          </span>
        </nz-form-control>

        <app-alert type="error" *ngIf="errors" [errorKeys]="errors"></app-alert>
        <mac-form-input-file [accept]="['Csv']" [fixedHeight]="154" [fileType]="'file'" (fileChange)="uploadBulkFile($event)" [isLoading]="isLoading">
        </mac-form-input-file>
      </form>
    </mac-modal-content>
    <mac-modal-footer>
      <mac-modal-cancel-button class="cancelButton" (cancel)="closeModal()" />
    </mac-modal-footer>
  </mac-modal>`,
})
export class EditUsersBulkModalComponent {
  @Input() isVisible: boolean = false;
  @Input() bulkUpdateUsersAction: (file: File) => Observable<UpdateBulkUsersResponse>;

  @Output() usersUpdated = new EventEmitter<void>();
  @Output() bulkUsersDeleteModalClosed = new EventEmitter<boolean>();

  @ViewChild('modal') modal: ModalComponent;

  errors: ExpectedError[] = [];
  isLoading: boolean = false;
  public errorKeys: string[] = [];

  constructor(private nzNotificationService: NzNotificationService, private translateService: TranslateService) {}

  openModal(): void {
    this.modal.openModal();
  }

  closeModal(): void {
    this.resetAllFormControls();
    this.modal.closeModal();
  }

  resetAllFormControls(): void {
    this.errors = [];
  }

  uploadBulkFile(file: File) {
    this.errors = [];
    if (file.name.split('.').pop()?.toLowerCase() === 'csv') {
      this.isLoading = true;
      this.bulkUpdateUsersAction(file).subscribe({
        next: (response: UpdateBulkUsersResponse) => {
          if (response.errorCount === 0) {
            this.nzNotificationService.success(
              '',
              this.translateService.instant('Portal.InspectionCompanies.Users.All.BulkUpdateUsers.Success.Message', { COUNT: response.successCount }),
              { nzPlacement: 'bottom' }
            );
          } else {
            this.nzNotificationService.error(
              '',
              this.translateService.instant('Portal.InspectionCompanies.Users.All.BulkUpdateUsers.Error.Message', {
                SUCCESSCOUNT: response.successCount,
                ERRORCOUNT: response.errorCount,
              }),
              { nzDuration: 0, nzPlacement: 'bottom' }
            );
            // open file in response.file
            this.downloadFile(response.errorList);
          }
          this.isLoading = false;
          this.usersUpdated.emit();
        },
        error: (response: HttpErrorResponse) => {
          this.errors = response.error.errors;
          this.isLoading = false;
        },
      });
    } else {
      this.errors = ['FILE_NOT_IN_CORRECT_FORMAT'];
    }
  }

  downloadFile(errorList: UsersToUpdate[]) {
    const file = new Blob(
      [
        'Email;New email;New roles;New Culture;New Firstname;New Lastname\n'.concat(
          errorList
            .map(
              (user) =>
                `${user.email};${user.newEmail};${user.newRoles.join(', ')};${user.newCulture};${user.newFirstName};${
                  user.newLastName
                };${this.translateService.instant('Portal.InspectionCompanies.Users.All.BulkUpdateUsers.Error.'.concat(user.error))}\n`
            )
            .join('')
        ),
      ],
      { type: 'text/csv' }
    );
    const data = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = data;
    link.download = 'edit-users.csv'; // set a name for the file
    link.click();
  }

  downloadBulkExample() {
    const file = new Blob(
      [
        'Email;NewEmail;NewRoles;NewCulture;NewFirstName;NewLastName\nmax.mustermann@example.de;maks.mustermann@example.de;Inspector,Editor,Inspection portal user,Back office user,Inspection Company Owner;De-De;Maks;Mustermann\nmax.musterman@test.de;;Inactive;;;',
      ],
      { type: 'text/csv' }
    );
    const data = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = data;
    link.download = 'edit-users.csv'; // set a name for the file
    link.click();
  }
}
